import { merge } from "./Merge.js";
import { Signal } from "./Signal.js";

export interface LanguageInfo {
    /**
     * Ordered list of fallback language codes.
     */
    fallbacks: LanguageCode[];
}

/**
 * ISO 639-1 language codes
 */
export type LanguageCode = "de" | "en" | "fr" | "uk";

export interface LanguageOptions {
    current?: LanguageCode | "html" | "user";
    activeLanguages?: LanguageCode[];
    fallbacks?: { [code: string]: LanguageCode[] };
    fallbackLanguage?: LanguageCode;
}

export class Language {
    public readonly onChange = Signal.create<[Language]>();

    private _activeLanguages?: LanguageCode[] = null;
    private _current: LanguageCode;
    private _fallbacks: { [code: string]: LanguageCode[] } = {
        "de": ["en"],
        "en": ["de"],
        "fr": ["en"],
        "uk": ["en"]
    };

    constructor(options: LanguageOptions = {}) {
        options = merge(
            {
                current: "html",
                fallbackLanguage: "de"
            },
            options);

        if (options.activeLanguages
            && options.activeLanguages.length > 0
        ) {
            this._activeLanguages = options.activeLanguages.slice();
        }

        /**
         * Resolve the current language code from context or options.
         */
        let current: LanguageCode = null;
        if (options.current === "html") {
            //RFC 5646
            const locale = document.documentElement.getAttribute("lang");
            if (locale) {
                current = locale.toLowerCase().substring(0, 2) as LanguageCode;
            }
            /**
             * Get language code from parent/top window if current language code is not set.
             */
            else if (window.top !== window.self) {
                try {
                    const locale = window.top.document.documentElement.getAttribute("lang");
                    if (locale) {
                        current = locale.toLowerCase().substring(0, 2) as LanguageCode;
                    }
                } catch (e) { }
            }

        }
        else if (options.current === "user") {
            current = navigator.language.toLowerCase().substring(0, 2) as LanguageCode;
        } else {
            current = options.current;
        }

        if (!current) {
            /**
             * Fallback to first supported language code if current language code is not supported.
             */
            if (this._activeLanguages) {
                current = this._activeLanguages[0];
                console.warn(`Language not resolved.`
                    + ` Fallback to first supported language code "${current}".`);
            } else {
                current = options.fallbackLanguage;
                console.warn(`Language not resolved.`
                    + ` Fallback to "${current}".`);
            }
        }

        if (this._activeLanguages
            && this._activeLanguages.indexOf(current) === -1) {
            console.warn(`Cannot set language code "${current}" because it is not included in the active languages.`
                + ` Fallback to first supported language code "${this._activeLanguages[0]}".`
            );
            current = this._activeLanguages[0];
        }

        if (options.fallbacks) {
            for (let code in options.fallbacks) {
                this.setFallbacks(code as LanguageCode, options.fallbacks[code]);
            }
        }

        this._current = current;
    }


    public get current(): LanguageCode {
        return this._current;
    }

    public set current(code: LanguageCode) {
        if (this._activeLanguages
            && this._activeLanguages.indexOf(code) === -1) {
            console.warn(`Cannot set language code "${code}" because it is not included in the active languages.`);
            return;
        }

        if (this._current === code) {
            return;
        }
        this._current = code;

        this.onChange.dispatch(this);
    }

    public setFallbacks(code: LanguageCode, fallbacks: LanguageCode[]): void {
        this._fallbacks[code] = fallbacks.slice();
    }

    public getFallbacks(code: LanguageCode): LanguageCode[] {
        if (!this._fallbacks[code]) {
            return [];
        }
        return this._fallbacks[code].slice();
    }
}

export const lang = new Language();